import {
  Col,
  Collapse,
  message,
  PageHeader,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Tabs,
  Tag,
} from "antd";
import axios from "axios";
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  ButtonItem,
  ErrorItem,
  Form,
  FormItem,
  RangePickerItem,
  SelectItem,
  TableItem,
  dineroDisabledMask,
} from "../../../../Components/Items";
import { FileExcelOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import * as XLSXStyle from "xlsx-js-style";
import moment from "moment";
import "moment/locale/es";
moment.locale("es");

const { Option } = Select;
const { TabPane } = Tabs;
const { Panel } = Collapse;

// Formato Contabilidad: el signo de pesos queda pegado al borde izquierdo de la
// celda y el numero al borde derecho, en vez de ir junto al numero.
const FORMATO_CONTABILIDAD = '_-"$"* #,##0.00_-;-"$"* #,##0.00_-;_-"$"* "-"??_-;_-@_-';
const FORMATO_CANTIDAD = "#,##0.00";
const FORMATO_PORCENTAJE = "0.00%";

const COLOR_FISCAL = "CF1322";

const dinero = (valor) => dineroDisabledMask(valor || 0);

// La base de una retencion puede no existir: en compras no se guarda y hay que
// derivarla de la tasa, y hay casos en los que la tasa no se puede identificar.
// `dinero` convertiria ese hueco en "$ 0.00", que seria una cifra inventada.
const dineroOpcional = (valor) =>
  valor === null || valor === undefined ? "—" : dinero(valor);

const numero = (valor) =>
  (valor || 0).toLocaleString("es-MX", { maximumFractionDigits: 4 });

const porcentaje = (valor) =>
  valor === null || valor === undefined ? "—" : `${numero(valor)}%`;

const sumaCampo = (filas, campo) =>
  (filas || []).reduce((acumulado, fila) => acumulado + (Number(fila[campo]) || 0), 0);

// El papel de trabajo trae sus retenciones como arreglo; el resumen ya las trae
// indexadas por descripcion.
const valorRetencion = (fila, descripcion, campo) => {
  const encontrada = (fila.retenciones || []).find(
    (valueRetencion) => valueRetencion.descripcion === descripcion
  );
  return encontrada ? encontrada[campo] : null;
};

// Columnas del papel de trabajo en el Excel: primero el bloque del documento y
// despues el bloque de insumos, para que un solo renglon de encabezado cubra los
// dos niveles y el archivo se pueda filtrar/ordenar como una tabla plana.
//
// En medio va un bloque dinamico con una pareja de columnas (base e importe) por
// cada retencion que aparecio en el periodo, por eso los encabezados y los anchos
// son funciones y no arreglos fijos.
const ENCABEZADOS_DOCUMENTO = [
  "Tipo",
  "Fecha Pago",
  "Pago",
  "Proveedor",
  "RFC",
  "Folio Factura",
  "UUID",
  "Fecha Factura",
  "Descripcion",
  "Moneda",
  "T.C.",
  "Subtotal Factura",
  "Impuestos Factura",
  "Retenciones Factura",
  "Total Factura",
  "Total a Pagar",
  "Monto Pagado",
  "% Aplicado",
  // Lleva el sufijo "Factura" porque el bloque de insumos trae su propio
  // "Subtotal Prorrateado" y dos columnas homonimas en la misma hoja plana
  // ensucian cualquier filtro o tabla dinamica.
  "Subtotal Prorrateado Factura",
];

const ENCABEZADOS_RETENCION = [
  "IVA Retenido",
  "ISR Retenido",
  "Otras Retenciones",
  "Total Retenido",
  "Aplicado A",
];

const ENCABEZADOS_INSUMO = [
  "Clave",
  "Codigo",
  "Descripcion Insumo",
  "Unidad",
  "Cantidad",
  "Precio Unitario",
  "Subtotal Insumo",
  "Impuestos Insumo",
  "Total Insumo",
  "Cantidad Prorrateada",
  "Subtotal Prorrateado",
  "Impuestos Prorrateados",
  "Total Prorrateado",
  "Retencion Prorrateada",
];

const encabezadosPapel = (descripciones) => [
  ...ENCABEZADOS_DOCUMENTO,
  ...descripciones.flatMap((descripcion) => [`Base ${descripcion}`, descripcion]),
  ...ENCABEZADOS_RETENCION,
  ...ENCABEZADOS_INSUMO,
];

// Cuantas columnas ocupa el renglon del documento: el bloque de insumos empieza
// despues, y el relleno de celdas vacias de los dos tipos de renglon sale de aqui.
const columnasDocumento = (descripciones) =>
  ENCABEZADOS_DOCUMENTO.length + descripciones.length * 2 + ENCABEZADOS_RETENCION.length;

const anchosPapel = (descripciones) => [
  13, 12, 12, 34, 15, 16, 38, 13, 28, 8, 8,
  16, 17, 18, 16, 16, 16, 11, 20,
  ...descripciones.flatMap(() => [22, 20]),
  15, 15, 17, 16, 16,
  14, 10, 34, 10, 13, 15, 16, 16, 16, 19, 19, 21, 18, 21,
];

// Detalle de retenciones que se abre con cada renglon del papel de trabajo. Va
// aqui y no en columnas planas porque la tabla del papel ya no tiene ancho libre.
const COLUMNAS_RETENCIONES = [
  { title: "Retencion", dataIndex: "descripcion", key: "descripcion", width: 260 },
  {
    title: "Tasa", dataIndex: "tasa", key: "tasa", width: 110, align: "right",
    render: porcentaje,
  },
  {
    title: "Base", dataIndex: "base", key: "base", width: 150, align: "right",
    render: dineroOpcional,
  },
  {
    title: "Retenido", dataIndex: "monto", key: "monto", width: 150, align: "right",
    render: (valor) => <b>{dinero(valor)}</b>,
  },
];

const COLUMNAS_INSUMOS = [
  { title: "Clave", dataIndex: "clave", key: "clave", width: 110 },
  { title: "Codigo", dataIndex: "codigo", key: "codigo", width: 90 },
  { title: "Descripcion", dataIndex: "descripcion", key: "descripcion", width: 260, ellipsis: true },
  { title: "Unidad", dataIndex: "unidad", key: "unidad", width: 90 },
  { title: "Cantidad", dataIndex: "cantidad", key: "cantidad", width: 100, align: "right", render: numero },
  { title: "P. Unitario", dataIndex: "precioUnitario", key: "precioUnitario", width: 120, align: "right", render: dinero },
  { title: "Subtotal", dataIndex: "subtotal", key: "subtotal", width: 120, align: "right", render: dinero },
  { title: "Impuestos", dataIndex: "impuestos", key: "impuestos", width: 120, align: "right", render: dinero },
  { title: "Total", dataIndex: "total", key: "total", width: 120, align: "right", render: dinero },
  { title: "Cant. Prorrateada", dataIndex: "cantidadProrrateada", key: "cantidadProrrateada", width: 135, align: "right", render: numero },
  { title: "Subtotal Prorrateado", dataIndex: "subtotalProrrateado", key: "subtotalProrrateado", width: 155, align: "right", render: dinero },
  { title: "Impuestos Prorrateados", dataIndex: "impuestosProrrateados", key: "impuestosProrrateados", width: 170, align: "right", render: dinero },
  { title: "Total Prorrateado", dataIndex: "totalProrrateado", key: "totalProrrateado", width: 145, align: "right", render: dinero },
  {
    title: "Retencion Prorrateada", dataIndex: "retencionProrrateada", key: "retencionProrrateada", width: 170, align: "right",
    render: (valor) => <b>{dinero(valor)}</b>,
  },
];

// Fuente unica de la leyenda: se muestra en el panel "Leyenda" de la pantalla y
// se escribe tal cual en la hoja "Leyenda" del Excel.
const LEYENDA = [
  {
    seccion: "Cómo se arma el reporte",
    campos: [
      {
        campo: "Criterio de flujo",
        significado:
          "El reporte se para en los PAGOS del periodo, no en las facturas. Una factura de enero pagada en marzo aparece en marzo, y solo por la parte que se pagó.",
      },
      {
        campo: "Prorrateo",
        significado:
          "Si un pago liquida solo una parte de la factura, se toma esa misma proporción de su retención. Al terminar de pagarla, la suma de todos sus renglones da la retención completa de la factura.",
      },
      {
        campo: "Notas de crédito",
        significado:
          "Cuando se aplica una nota de crédito, su propia retención se resta (renglón en rojo y en negativo). El total por proveedor ya queda neto.",
      },
      {
        campo: "Moneda",
        significado:
          "Todos los importes se presentan en moneda de contabilidad, convertidos con el tipo de cambio del documento.",
      },
      {
        campo: "De dónde sale la base",
        significado:
          "La factura de compra sólo guarda el importe retenido, no la base. La base se calcula dividiendo ese importe entre la tasa de la retención del catálogo de la empresa. Si la tasa no se puede identificar —porque la retención se borró del catálogo, o porque la empresa tiene dos con el mismo nombre y no se sabe cuál se aplicó—, la celda queda vacía (—), nunca en cero.",
      },
      {
        campo: "Bases que no se suman",
        significado:
          "La base de cada retención se puede sumar entre documentos, pero NO entre retenciones distintas: el IVA y el ISR retenidos de una misma factura se calculan sobre el mismo subtotal, así que sumarlos lo contaría dos veces. Por eso no hay una columna de base total.",
      },
      {
        campo: "Retenciones en cero",
        significado:
          "Una retención que quedó en importe cero no aparece en el reporte, ni siquiera con su base.",
      },
    ],
  },
  {
    seccion: "Resumen por Proveedor",
    nota:
      "Además aparece una columna por cada retención que se usó en el periodo, con el nombre con el que está dada de alta en el catálogo (por ejemplo Retencion IVA). Si la empresa tiene dos retenciones del mismo impuesto con tasas distintas (IVA 10.6667% y IVA 4%), el catálogo les pone el mismo nombre y el reporte las presenta juntas en una sola columna.",
    campos: [
      { campo: "Proveedor", significado: "Proveedor al que se le retuvo." },
      { campo: "RFC", significado: "RFC del proveedor según el catálogo." },
      {
        campo: "Documentos",
        significado:
          "Cuántas facturas y notas de crédito distintas de ese proveedor tuvieron movimiento de pago en el periodo. Una factura pagada en tres exhibiciones cuenta una sola vez.",
      },
      {
        campo: "Monto Pagado",
        significado:
          "Lo que se le pagó al proveedor en el periodo sobre esos documentos. Las notas de crédito aplicadas restan.",
      },
      {
        campo: "Subtotal Pagado",
        significado:
          "Subtotal de los documentos (sin impuestos ni retenciones), prorrateado a la parte que se pagó en el periodo. Es el subtotal de la factura, no la base de una retención en particular.",
      },
      {
        campo: "Base de cada retención",
        significado:
          "Importe sobre el que se calculó esa retención, prorrateado a lo pagado: base × tasa = retención. En pantalla va en gris debajo del importe retenido; en el Excel es una columna aparte, a la izquierda de la retención. Un asterisco (*) avisa que el acumulado está incompleto porque alguno de los documentos de ese proveedor no pudo resolver su tasa.",
      },
      {
        campo: "IVA Retenido",
        significado:
          "Retenciones de IVA prorrateadas a lo pagado. En compras se identifican por el texto de la retención.",
      },
      { campo: "ISR Retenido", significado: "Retenciones de ISR prorrateadas a lo pagado." },
      {
        campo: "Otras Retenciones",
        significado: "Cualquier retención que no sea IVA ni ISR (cedular, estatal, etc.).",
      },
      {
        campo: "Total Retenido",
        significado:
          "IVA + ISR + otras. Es la cifra que se le retuvo a ese proveedor en el periodo.",
      },
    ],
  },
  {
    seccion: "Papel de trabajo (un renglón por pago aplicado a un documento)",
    nota:
      "Las columnas de la factura van al 100% y por eso no se totalizan: una factura pagada en varias exhibiciones aparece en varios renglones. Lo que sí se totaliza son las columnas prorrateadas.",
    campos: [
      {
        campo: "Tipo",
        significado:
          "Factura: suma retención. NC (nota de crédito): resta, y todo el renglón va en negativo.",
      },
      {
        campo: "Fecha Pago",
        significado: "Fecha del pago. Es la que decide si el movimiento cae dentro del periodo.",
      },
      { campo: "Pago", significado: "Folio (serie-número) del pago con el que se liquidó. Solo en el Excel." },
      { campo: "Proveedor / RFC", significado: "Proveedor del documento que se está pagando." },
      {
        campo: "Folio Factura",
        significado:
          "Folio de la factura del proveedor; si el documento no lo tiene capturado, se muestra el consecutivo interno.",
      },
      { campo: "UUID", significado: "Folio fiscal del CFDI. Completo en el Excel, abreviado en pantalla." },
      {
        campo: "Fecha Factura",
        significado: "Fecha de emisión de la factura. No filtra el periodo, solo informa.",
      },
      { campo: "Descripción", significado: "Descripción capturada en la factura. Solo en el Excel." },
      {
        campo: "Moneda / T.C.",
        significado:
          "Moneda y tipo de cambio del documento. Solo en el Excel; los importes ya vienen convertidos.",
      },
      { campo: "Subtotal Factura", significado: "Subtotal de la factura completa, al 100%." },
      {
        campo: "Impuestos Factura",
        significado: "Impuestos trasladados de la factura completa, al 100%. Solo en el Excel.",
      },
      {
        campo: "Retenciones Factura",
        significado: "Retenciones de la factura completa, al 100%. Solo en el Excel.",
      },
      { campo: "Total Factura", significado: "Subtotal + impuestos de la factura completa, al 100%." },
      {
        campo: "Total a Pagar",
        significado:
          "Total de la factura menos sus retenciones: lo que realmente se le paga al proveedor. Es el denominador del prorrateo.",
      },
      {
        campo: "Monto Pagado",
        significado: "Lo que este pago aplicó a este documento. Negativo si es nota de crédito.",
      },
      {
        campo: "% Aplicado",
        significado:
          "Monto Pagado entre Total a Pagar: qué parte del documento liquidó este pago. Es el factor con el que se prorratea todo lo demás.",
      },
      { campo: "Subtotal Prorrateado Factura", significado: "Subtotal de la factura por el % aplicado. Antes se llamaba \"Base Prorrateada\": se renombró porque no es la base de ninguna retención en particular." },
      {
        campo: "Base <retención> / <retención>",
        significado:
          "Sólo en el Excel: por cada retención del periodo van dos columnas pareadas, la base y el importe retenido, las dos prorrateadas al pago. En pantalla ese mismo detalle se abre con la flecha del renglón.",
      },
      { campo: "IVA Retenido", significado: "Retención de IVA de la factura por el % aplicado." },
      { campo: "ISR Retenido", significado: "Retención de ISR de la factura por el % aplicado." },
      { campo: "Otras Retenciones", significado: "Resto de retenciones de la factura por el % aplicado." },
      {
        campo: "Total Retenido",
        significado:
          "Retención total de la factura por el % aplicado. La suma de esta columna es el total del reporte.",
      },
      {
        campo: "Aplicado A",
        significado: "Solo en notas de crédito: folio de la factura a la que se aplicó la nota.",
      },
    ],
  },
  {
    seccion: "Detalle de retenciones (qué se le retuvo a esa factura)",
    nota:
      "Se abre con la flecha de cada renglón del papel de trabajo, arriba del detalle de insumos. En el Excel este mismo desglose va en columnas, dentro del renglón.",
    campos: [
      {
        campo: "Retención",
        significado: "Nombre con el que la retención está dada de alta en el catálogo de la empresa.",
      },
      {
        campo: "Tasa",
        significado:
          "Porcentaje con el que se calculó. Es la tasa de la retención, no se prorratea. Vacía (—) si no se pudo identificar.",
      },
      {
        campo: "Base",
        significado:
          "Importe sobre el que se calculó la retención, prorrateado al pago. Base × tasa = retenido. Vacía (—) cuando no se pudo identificar la tasa.",
      },
      {
        campo: "Retenido",
        significado: "Importe retenido por ese concepto, prorrateado al pago. La suma de esta columna es el Total Retenido del renglón.",
      },
    ],
  },
  {
    seccion: "Detalle de insumos (lo que venía dentro de la factura)",
    nota:
      "Se abre con la flecha de cada renglón del papel de trabajo, debajo del detalle de retenciones; en el Excel va debajo de su renglón, en las columnas de la derecha.",
    campos: [
      {
        campo: "Clave / Código",
        significado:
          "Clave y código del insumo. Si la factura se capturó con el alta que el proveedor tiene del insumo, se muestra el código del proveedor.",
      },
      { campo: "Descripción / Unidad", significado: "Descripción y unidad de medida del insumo." },
      {
        campo: "Cantidad, P. Unitario, Subtotal, Impuestos, Total",
        significado: "Los valores reales del renglón en la factura, al 100%. Sirven para ver qué se compró.",
      },
      {
        campo: "Cant. / Subtotal / Impuestos / Total Prorrateados",
        significado:
          "Los mismos valores multiplicados por el % aplicado, para que cuadren contra el monto pagado.",
      },
      {
        campo: "Retención Prorrateada",
        significado:
          "El Total Retenido del renglón repartido entre los insumos en proporción a su subtotal. La suma de esta columna da el Total Retenido del pago.",
      },
    ],
  },
];

const COLUMNAS_LEYENDA = [
  { title: "Campo", dataIndex: "campo", key: "campo", width: 260 },
  { title: "Qué significa", dataIndex: "significado", key: "significado" },
];

class ReporteRetencionesCompras extends Component {
  formRef = React.createRef();

  state = {
    loading: false,
    dataReporte: null,
    objetoProveedores: null,
    periodo: [moment().startOf("month"), moment().endOf("month")],
  };

  atras() {
    this.props.history.goBack();
  }

  async componentDidMount() {
    this.setState({ loading: true });
    await axios
      .post(
        "proveedores/selectProveedores",
        { empresaId: this.props.empresaId },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.status !== 404) {
          this.setState({
            objetoProveedores: res.data.map((value) => (
              <Option value={value._id} key={value._id}>
                {value.stringBuscar || value.razonSocial || value.nombre}
              </Option>
            )),
          });
        }
      });
    this.consultarReporte({
      periodo: [moment().startOf("month"), moment().endOf("month")],
      proveedores: [],
    });
  }

  consultarReporte(values) {
    this.setState({ loading: true, periodo: values.periodo });
    axios
      .post(
        "reportesFiscales/reporteRetencionesCompras",
        {
          empresaId: this.props.empresaId,
          periodo: values.periodo,
          proveedores: values.proveedores || [],
        },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.success === 2) {
          this.setState({ dataReporte: res.data, loading: false });
        } else {
          message.error("Ha sucedido un error y no se cargo bien el reporte");
          this.setState({ loading: false });
        }
      })
      .catch(() => {
        message.error("Error de conexion");
        this.setState({ loading: false });
      });
  }

  onFinishFailed(e) {
    ErrorItem(e);
  }

  // Las columnas de cada retencion se arman con las descripciones que realmente
  // aparecieron en el periodo (Retencion IVA, Retencion ISR, cedular, etc.).
  columnasResumen() {
    const descripciones = this.state.dataReporte?.descripcionesRetencion || [];
    return [
      { title: "Proveedor", dataIndex: "nombre", key: "nombre", width: 240 },
      { title: "RFC", dataIndex: "rfc", key: "rfc", width: 130 },
      { title: "Documentos", dataIndex: "documentos", key: "documentos", width: 105, align: "right" },
      { title: "Monto Pagado", dataIndex: "montoPagado", key: "montoPagado", width: 135, align: "right", render: dinero },
      { title: "Subtotal Pagado", dataIndex: "subtotal", key: "subtotal", width: 140, align: "right", render: dinero },
      // La base va debajo del importe y no en columna aparte: la tabla reparte el
      // ancho entre las columnas y no tiene scroll horizontal en escritorio, asi
      // que duplicar este bloque cortaria los importes. En el Excel si van pareadas.
      ...descripciones.map((descripcion) => ({
        title: descripcion,
        key: `desglose-${descripcion}`,
        descripcionRetencion: descripcion,
        width: 155,
        align: "right",
        render: (_, registro) => {
          const base = registro.desgloseBase && registro.desgloseBase[descripcion];
          const parcial =
            registro.desgloseBaseIncompleta && registro.desgloseBaseIncompleta[descripcion];
          return (
            <div style={{ whiteSpace: "normal" }}>
              {dinero(registro.desglose && registro.desglose[descripcion])}
              <div style={{ fontSize: 11, color: "#8c8c8c", lineHeight: 1.2 }}>
                {`base ${dineroOpcional(base)}${parcial ? " *" : ""}`}
              </div>
            </div>
          );
        },
      })),
      { title: "IVA Retenido", dataIndex: "retIva", key: "retIva", width: 135, align: "right", render: dinero },
      { title: "ISR Retenido", dataIndex: "retIsr", key: "retIsr", width: 135, align: "right", render: dinero },
      { title: "Otras Retenciones", dataIndex: "retOtras", key: "retOtras", width: 145, align: "right", render: dinero },
      {
        title: "Total Retenido", dataIndex: "retTotal", key: "retTotal", width: 145, align: "right",
        render: (valor) => <b>{dinero(valor)}</b>,
      },
    ];
  }

  // En pantalla se muestran las columnas indispensables; el desglose completo
  // (UUID, descripcion, moneda, tipo de cambio, insumos) va en el Excel.
  columnasPapel() {
    return [
      {
        title: "Tipo", dataIndex: "tipo", key: "tipo", width: 95,
        render: (valor) =>
          valor === "NOTA_CREDITO" ? <Tag color="red">NC</Tag> : <Tag color="green">Factura</Tag>,
      },
      {
        title: "Fecha Pago", dataIndex: "fechaPago", key: "fechaPago", width: 105,
        render: (valor) => (valor ? moment(valor).format("DD/MM/YYYY") : ""),
      },
      { title: "Proveedor", dataIndex: "nombre", key: "nombre", width: 210 },
      { title: "Folio Factura", dataIndex: "folio", key: "folio", width: 125 },
      {
        title: "Fecha Factura", dataIndex: "fechaDocumento", key: "fechaDocumento", width: 115,
        render: (valor) => (valor ? moment(valor).format("DD/MM/YYYY") : ""),
      },
      // Las columnas del documento no se totalizan: una factura pagada en varias
      // exhibiciones aparece en varios renglones y sumarlas la contaria de mas.
      { title: "Subtotal Factura", dataIndex: "subtotalDoc", key: "subtotalDoc", width: 135, align: "right", sinTotal: true, render: dinero },
      { title: "Total Factura", dataIndex: "totalDoc", key: "totalDoc", width: 130, align: "right", sinTotal: true, render: dinero },
      { title: "Total a Pagar", dataIndex: "totalPagarDoc", key: "totalPagarDoc", width: 130, align: "right", sinTotal: true, render: dinero },
      { title: "Monto Pagado", dataIndex: "montoPagado", key: "montoPagado", width: 130, align: "right", render: dinero },
      {
        title: "% Aplicado", dataIndex: "factor", key: "factor", width: 100, align: "right",
        render: (valor) => `${((valor || 0) * 100).toFixed(2)}%`,
      },
      { title: "IVA Retenido", dataIndex: "retIva", key: "retIva", width: 130, align: "right", render: dinero },
      { title: "ISR Retenido", dataIndex: "retIsr", key: "retIsr", width: 130, align: "right", render: dinero },
      { title: "Otras Retenciones", dataIndex: "retOtras", key: "retOtras", width: 145, align: "right", render: dinero },
      {
        title: "Total Retenido", dataIndex: "retTotal", key: "retTotal", width: 140, align: "right",
        render: (valor) => <b>{dinero(valor)}</b>,
      },
    ];
  }

  // `conExpandible` reserva la primera celda para la columna del expansor, que
  // antd inserta antes de las columnas declaradas cuando la tabla es expandible.
  filaResumenTotales(columnas, filas, conExpandible) {
    const desplazamiento = conExpandible ? 1 : 0;
    return (
      <TableItem.Summary.Row>
        {conExpandible && <TableItem.Summary.Cell key="expansor" index={0} />}
        {columnas.map((columna, indice) => {
          const posicion = indice + desplazamiento;
          if (indice === 0) {
            return (
              <TableItem.Summary.Cell key={columna.key} index={posicion}>
                <b>TOTAL</b>
              </TableItem.Summary.Cell>
            );
          }
          if (columna.align !== "right" || columna.sinTotal || columna.dataIndex === "factor") {
            return <TableItem.Summary.Cell key={columna.key} index={posicion} />;
          }
          const total = columna.descripcionRetencion
            ? filas.reduce(
                (acumulado, fila) =>
                  acumulado + ((fila.desglose && fila.desglose[columna.descripcionRetencion]) || 0),
                0
              )
            : sumaCampo(filas, columna.dataIndex);
          return (
            <TableItem.Summary.Cell key={columna.key} index={posicion} align="right">
              <b>{columna.dataIndex === "documentos" ? total : dinero(total)}</b>
            </TableItem.Summary.Cell>
          );
        })}
      </TableItem.Summary.Row>
    );
  }

  // ── Excel ──────────────────────────────────────────────────────────────────

  descargarExcel() {
    const { dataReporte, periodo } = this.state;
    if (!dataReporte || dataReporte.papelTrabajo.length === 0) {
      message.warning("No hay retenciones en el periodo");
      return;
    }

    const border = {
      top: { style: "thin", color: { auto: 1 } },
      bottom: { style: "thin", color: { auto: 1 } },
      left: { style: "thin", color: { auto: 1 } },
      right: { style: "thin", color: { auto: 1 } },
    };
    const tituloStyle = { font: { bold: true, sz: 14 } };
    const periodoStyle = { font: { bold: true, sz: 11 } };
    const headerStyle = {
      font: { bold: true, sz: 10, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: COLOR_FISCAL } },
      alignment: { horizontal: "center", vertical: "center", wrapText: true },
      border,
    };
    const textoStyle = { border };
    const moneyStyle = { border, alignment: { horizontal: "right" }, numFmt: FORMATO_CONTABILIDAD };
    const cantidadStyle = { border, alignment: { horizontal: "right" }, numFmt: FORMATO_CANTIDAD };
    const totalTextoStyle = { font: { bold: true, sz: 10 }, fill: { fgColor: { rgb: "D9D9D9" } }, border };
    const totalMoneyStyle = { ...totalTextoStyle, alignment: { horizontal: "right" }, numFmt: FORMATO_CONTABILIDAD };
    const totalCantidadStyle = { ...totalTextoStyle, alignment: { horizontal: "right" }, numFmt: FORMATO_CANTIDAD };

    const estilosDocumento = {
      texto: { font: { bold: true, sz: 10 }, fill: { fgColor: { rgb: "F2F2F2" } }, border },
    };
    estilosDocumento.moneda = { ...estilosDocumento.texto, alignment: { horizontal: "right" }, numFmt: FORMATO_CONTABILIDAD };
    estilosDocumento.cantidad = { ...estilosDocumento.texto, alignment: { horizontal: "right" }, numFmt: FORMATO_CANTIDAD };
    estilosDocumento.porcentaje = { ...estilosDocumento.texto, alignment: { horizontal: "right" }, numFmt: FORMATO_PORCENTAJE };

    const estilosNota = {
      texto: { font: { bold: true, sz: 10, color: { rgb: COLOR_FISCAL } }, fill: { fgColor: { rgb: "FDE7E9" } }, border },
    };
    estilosNota.moneda = { ...estilosNota.texto, alignment: { horizontal: "right" }, numFmt: FORMATO_CONTABILIDAD };
    estilosNota.cantidad = { ...estilosNota.texto, alignment: { horizontal: "right" }, numFmt: FORMATO_CANTIDAD };
    estilosNota.porcentaje = { ...estilosNota.texto, alignment: { horizontal: "right" }, numFmt: FORMATO_PORCENTAJE };

    const insumoTextoStyle = { font: { sz: 9, color: { rgb: "595959" } }, border };
    const insumoMoneyStyle = { ...insumoTextoStyle, alignment: { horizontal: "right" }, numFmt: FORMATO_CONTABILIDAD };
    const insumoCantidadStyle = { ...insumoTextoStyle, alignment: { horizontal: "right" }, numFmt: FORMATO_CANTIDAD };

    // xlsx-js-style no infiere el tipo cuando la celda ya viene armada como
    // { v, s }: sin el t: "n" explicito el numero se escribe como texto y el
    // formato de moneda nunca se aplica. Las celdas en blanco conservan el
    // estilo para que al capturar un monto a mano ya salga en Contabilidad.
    const celdaMoneda = (valor, estilo) => (valor ? { v: valor, t: "n", s: estilo } : { v: "", s: estilo });
    const celdaNumero = (valor, estilo) => (valor ? { v: valor, t: "n", s: estilo } : { v: "", s: estilo });
    const celdaTexto = (valor, estilo) => ({ v: valor || "", s: estilo });
    const celdasVacias = (cantidad, estilo) =>
      new Array(cantidad).fill(null).map(() => ({ v: "", s: estilo }));

    const nombreEmpresa = this.props.empresa || "";
    const textoPeriodo = `PERIODO: ${moment(periodo[0]).format("DD/MM/YYYY")} - ${moment(periodo[1]).format(
      "DD/MM/YYYY"
    )} (por fecha de pago)`;

    const libro = XLSXStyle.utils.book_new();

    // ── Hoja 1: Resumen por proveedor ──
    const descripciones = dataReporte.descripcionesRetencion || [];

    // En el Excel si hay ancho de sobra, asi que la base de cada retencion va en
    // su propia columna, pegada a la del importe retenido.
    const ENCABEZADOS_PAPEL = encabezadosPapel(descripciones);
    const COLUMNAS_DOCUMENTO = columnasDocumento(descripciones);

    const encabezadosResumen = [
      "Proveedor",
      "RFC",
      "Documentos",
      "Monto Pagado",
      "Subtotal Pagado",
      ...descripciones.flatMap((descripcion) => [`Base ${descripcion}`, descripcion]),
      "IVA Retenido",
      "ISR Retenido",
      "Otras Retenciones",
      "Total Retenido",
    ];

    const filasResumen = [];
    const mergesResumen = [];
    filasResumen.push([{ v: `RETENCIONES DE COMPRAS - ${nombreEmpresa}`, s: tituloStyle }]);
    mergesResumen.push({ s: { r: 0, c: 0 }, e: { r: 0, c: encabezadosResumen.length - 1 } });
    filasResumen.push([{ v: textoPeriodo, s: periodoStyle }]);
    mergesResumen.push({ s: { r: 1, c: 0 }, e: { r: 1, c: encabezadosResumen.length - 1 } });
    filasResumen.push([]);
    filasResumen.push(encabezadosResumen.map((titulo) => ({ v: titulo, s: headerStyle })));

    dataReporte.resumen.forEach((fila) => {
      filasResumen.push([
        celdaTexto(fila.nombre, textoStyle),
        celdaTexto(fila.rfc, textoStyle),
        celdaNumero(fila.documentos, cantidadStyle),
        celdaMoneda(fila.montoPagado, moneyStyle),
        celdaMoneda(fila.subtotal, moneyStyle),
        ...descripciones.flatMap((descripcion) => [
          celdaMoneda(fila.desgloseBase && fila.desgloseBase[descripcion], moneyStyle),
          celdaMoneda(fila.desglose && fila.desglose[descripcion], moneyStyle),
        ]),
        celdaMoneda(fila.retIva, moneyStyle),
        celdaMoneda(fila.retIsr, moneyStyle),
        celdaMoneda(fila.retOtras, moneyStyle),
        celdaMoneda(fila.retTotal, moneyStyle),
      ]);
    });

    filasResumen.push([
      { v: "TOTAL", s: totalTextoStyle },
      { v: "", s: totalTextoStyle },
      celdaNumero(sumaCampo(dataReporte.resumen, "documentos"), totalCantidadStyle),
      celdaMoneda(dataReporte.totales.montoPagado, totalMoneyStyle),
      celdaMoneda(dataReporte.totales.subtotal, totalMoneyStyle),
      ...descripciones.flatMap((descripcion) => [
        celdaMoneda(dataReporte.totales.desgloseBase[descripcion], totalMoneyStyle),
        celdaMoneda(dataReporte.totales.desglose[descripcion], totalMoneyStyle),
      ]),
      celdaMoneda(dataReporte.totales.retIva, totalMoneyStyle),
      celdaMoneda(dataReporte.totales.retIsr, totalMoneyStyle),
      celdaMoneda(dataReporte.totales.retOtras, totalMoneyStyle),
      celdaMoneda(dataReporte.totales.retTotal, totalMoneyStyle),
    ]);

    const hojaResumen = XLSXStyle.utils.aoa_to_sheet(filasResumen);
    hojaResumen["!merges"] = mergesResumen;
    hojaResumen["!cols"] = [
      { wch: 42 }, { wch: 16 }, { wch: 12 }, { wch: 16 }, { wch: 17 },
      ...descripciones.flatMap(() => [{ wch: 24 }, { wch: 20 }]),
      { wch: 16 }, { wch: 16 }, { wch: 18 }, { wch: 17 },
    ];
    XLSXStyle.utils.book_append_sheet(libro, hojaResumen, "Resumen");

    // ── Hoja 2: Papel de trabajo ──
    const filasPapel = [];
    const mergesPapel = [];
    filasPapel.push([{ v: `PAPEL DE TRABAJO - RETENCIONES DE COMPRAS - ${nombreEmpresa}`, s: tituloStyle }]);
    mergesPapel.push({ s: { r: 0, c: 0 }, e: { r: 0, c: ENCABEZADOS_PAPEL.length - 1 } });
    filasPapel.push([{ v: textoPeriodo, s: periodoStyle }]);
    mergesPapel.push({ s: { r: 1, c: 0 }, e: { r: 1, c: ENCABEZADOS_PAPEL.length - 1 } });
    filasPapel.push([]);
    filasPapel.push(ENCABEZADOS_PAPEL.map((titulo) => ({ v: titulo, s: headerStyle })));

    dataReporte.papelTrabajo.forEach((fila) => {
      const estilos = fila.tipo === "NOTA_CREDITO" ? estilosNota : estilosDocumento;

      filasPapel.push([
        celdaTexto(fila.tipo === "NOTA_CREDITO" ? "Nota Credito" : "Factura", estilos.texto),
        celdaTexto(fila.fechaPago ? moment(fila.fechaPago).format("DD/MM/YYYY") : "", estilos.texto),
        celdaTexto(fila.pagoFolio, estilos.texto),
        celdaTexto(fila.nombre, estilos.texto),
        celdaTexto(fila.rfc, estilos.texto),
        celdaTexto(fila.folio, estilos.texto),
        celdaTexto(fila.uuid, estilos.texto),
        celdaTexto(fila.fechaDocumento ? moment(fila.fechaDocumento).format("DD/MM/YYYY") : "", estilos.texto),
        celdaTexto(fila.descripcion, estilos.texto),
        celdaTexto(fila.moneda, estilos.texto),
        celdaNumero(fila.tipoDeCambio, estilos.cantidad),
        celdaMoneda(fila.subtotalDoc, estilos.moneda),
        celdaMoneda(fila.impuestosDoc, estilos.moneda),
        celdaMoneda(fila.retencionesDoc, estilos.moneda),
        celdaMoneda(fila.totalDoc, estilos.moneda),
        celdaMoneda(fila.totalPagarDoc, estilos.moneda),
        celdaMoneda(fila.montoPagado, estilos.moneda),
        celdaNumero(fila.factor, estilos.porcentaje),
        celdaMoneda(fila.subtotalProrrateado, estilos.moneda),
        ...descripciones.flatMap((descripcion) => [
          celdaMoneda(valorRetencion(fila, descripcion, "base"), estilos.moneda),
          celdaMoneda(valorRetencion(fila, descripcion, "monto"), estilos.moneda),
        ]),
        celdaMoneda(fila.retIva, estilos.moneda),
        celdaMoneda(fila.retIsr, estilos.moneda),
        celdaMoneda(fila.retOtras, estilos.moneda),
        celdaMoneda(fila.retTotal, estilos.moneda),
        celdaTexto(fila.aplicadoA, estilos.texto),
        ...celdasVacias(ENCABEZADOS_PAPEL.length - COLUMNAS_DOCUMENTO, estilos.texto),
      ]);

      fila.insumos.forEach((insumo) => {
        filasPapel.push([
          ...celdasVacias(COLUMNAS_DOCUMENTO, insumoTextoStyle),
          celdaTexto(insumo.clave, insumoTextoStyle),
          celdaTexto(insumo.codigo, insumoTextoStyle),
          celdaTexto(insumo.descripcion, insumoTextoStyle),
          celdaTexto(insumo.unidad, insumoTextoStyle),
          celdaNumero(insumo.cantidad, insumoCantidadStyle),
          celdaMoneda(insumo.precioUnitario, insumoMoneyStyle),
          celdaMoneda(insumo.subtotal, insumoMoneyStyle),
          celdaMoneda(insumo.impuestos, insumoMoneyStyle),
          celdaMoneda(insumo.total, insumoMoneyStyle),
          celdaNumero(insumo.cantidadProrrateada, insumoCantidadStyle),
          celdaMoneda(insumo.subtotalProrrateado, insumoMoneyStyle),
          celdaMoneda(insumo.impuestosProrrateados, insumoMoneyStyle),
          celdaMoneda(insumo.totalProrrateado, insumoMoneyStyle),
          celdaMoneda(insumo.retencionProrrateada, insumoMoneyStyle),
        ]);
      });
    });

    const filaTotalPapel = celdasVacias(ENCABEZADOS_PAPEL.length, totalTextoStyle);
    filaTotalPapel[0] = { v: "TOTAL", s: totalTextoStyle };
    filaTotalPapel[16] = celdaMoneda(dataReporte.totales.montoPagado, totalMoneyStyle);
    filaTotalPapel[18] = celdaMoneda(dataReporte.totales.subtotal, totalMoneyStyle);

    // El desglose por retencion arranca justo despues del subtotal prorrateado.
    // La base de UNA retencion si se suma entre documentos; sumarla entre
    // retenciones distintas no, porque el IVA y el ISR retenidos de una misma
    // factura salen del mismo subtotal. Por eso no hay total de bases.
    const inicioDesglose = ENCABEZADOS_DOCUMENTO.length;
    descripciones.forEach((descripcion, indice) => {
      filaTotalPapel[inicioDesglose + indice * 2] = celdaMoneda(
        dataReporte.totales.desgloseBase[descripcion],
        totalMoneyStyle
      );
      filaTotalPapel[inicioDesglose + indice * 2 + 1] = celdaMoneda(
        dataReporte.totales.desglose[descripcion],
        totalMoneyStyle
      );
    });

    const inicioRetenciones = inicioDesglose + descripciones.length * 2;
    filaTotalPapel[inicioRetenciones] = celdaMoneda(dataReporte.totales.retIva, totalMoneyStyle);
    filaTotalPapel[inicioRetenciones + 1] = celdaMoneda(dataReporte.totales.retIsr, totalMoneyStyle);
    filaTotalPapel[inicioRetenciones + 2] = celdaMoneda(dataReporte.totales.retOtras, totalMoneyStyle);
    filaTotalPapel[inicioRetenciones + 3] = celdaMoneda(dataReporte.totales.retTotal, totalMoneyStyle);
    filasPapel.push(filaTotalPapel);

    const hojaPapel = XLSXStyle.utils.aoa_to_sheet(filasPapel);
    hojaPapel["!merges"] = mergesPapel;
    hojaPapel["!cols"] = anchosPapel(descripciones).map((ancho) => ({ wch: ancho }));
    XLSXStyle.utils.book_append_sheet(libro, hojaPapel, "Papel de Trabajo");

    // ── Hoja 3: Leyenda ──
    const seccionStyle = {
      font: { bold: true, sz: 11, color: { rgb: "FFFFFF" } },
      fill: { fgColor: { rgb: COLOR_FISCAL } },
      alignment: { vertical: "center" },
      border,
    };
    const campoStyle = { font: { bold: true, sz: 10 }, alignment: { vertical: "top", wrapText: true }, border };
    const significadoStyle = { alignment: { vertical: "top", wrapText: true }, border };
    const notaStyle = { font: { italic: true, sz: 9, color: { rgb: "595959" } }, alignment: { wrapText: true } };

    const filasLeyenda = [];
    const mergesLeyenda = [];
    filasLeyenda.push([{ v: `LEYENDA - RETENCIONES DE COMPRAS - ${nombreEmpresa}`, s: tituloStyle }]);
    mergesLeyenda.push({ s: { r: 0, c: 0 }, e: { r: 0, c: 1 } });
    filasLeyenda.push([]);

    LEYENDA.forEach((seccion) => {
      filasLeyenda.push([
        { v: seccion.seccion, s: seccionStyle },
        { v: "", s: seccionStyle },
      ]);
      mergesLeyenda.push({
        s: { r: filasLeyenda.length - 1, c: 0 },
        e: { r: filasLeyenda.length - 1, c: 1 },
      });
      seccion.campos.forEach((campo) => {
        filasLeyenda.push([
          { v: campo.campo, s: campoStyle },
          { v: campo.significado, s: significadoStyle },
        ]);
      });
      if (seccion.nota) {
        filasLeyenda.push([{ v: seccion.nota, s: notaStyle }, { v: "", s: notaStyle }]);
        mergesLeyenda.push({
          s: { r: filasLeyenda.length - 1, c: 0 },
          e: { r: filasLeyenda.length - 1, c: 1 },
        });
      }
      filasLeyenda.push([]);
    });

    const hojaLeyenda = XLSXStyle.utils.aoa_to_sheet(filasLeyenda);
    hojaLeyenda["!merges"] = mergesLeyenda;
    hojaLeyenda["!cols"] = [{ wch: 34 }, { wch: 110 }];
    XLSXStyle.utils.book_append_sheet(libro, hojaLeyenda, "Leyenda");

    XLSXStyle.writeFile(
      libro,
      `Retenciones_Compras_${moment().locale("es").format("DD-MM-YYYY")}.xlsx`
    );
  }

  render() {
    const { loading, dataReporte } = this.state;
    const resumen = dataReporte ? dataReporte.resumen : [];
    const papelTrabajo = dataReporte ? dataReporte.papelTrabajo : [];
    const columnasResumen = this.columnasResumen();
    const columnasPapel = this.columnasPapel();

    return (
      <>
        <Form
          ref={this.formRef}
          name="reporteRetencionesCompras"
          onFinish={this.consultarReporte.bind(this)}
          onFinishFailed={this.onFinishFailed}
          initialValues={{
            periodo: [moment().startOf("month"), moment().endOf("month")],
            proveedores: [],
          }}
        >
          <PageHeader
            onBack={this.atras.bind(this)}
            title="Retenciones de Compras"
            style={{
              border: "1px solid rgb(235, 237, 240)",
              backgroundColor: "white",
              marginBottom: 10,
            }}
            extra={
              <Space>
                {dataReporte && (
                  <ButtonItem blanco icon={<FileExcelOutlined />} onClick={() => this.descargarExcel()}>
                    Descargar Excel
                  </ButtonItem>
                )}
                <ButtonItem type="primary" htmlType="submit">
                  Consultar
                </ButtonItem>
              </Space>
            }
          />

          <Spin spinning={loading}>
            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                paddingTop: 5,
                borderRadius: 5,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <Tabs
                style={{ margin: 0 }}
                tabBarExtraContent={{
                  left: <h1 style={{ padding: 0, margin: "0 10px 0 0" }}>Filtros:</h1>,
                }}
              >
                <TabPane tabKey="1" key="1" forceRender tab="Periodo">
                  <Row>
                    <Col span={12}>
                      <FormItem
                        name="periodo"
                        required
                        noStyle
                        tooltip="El periodo filtra por fecha de pago"
                      >
                        <RangePickerItem />
                      </FormItem>
                    </Col>
                  </Row>
                </TabPane>
                <TabPane tabKey="2" key="2" forceRender tab="Proveedores">
                  <Row>
                    <Col span={16}>
                      <FormItem name="proveedores" noStyle margin>
                        <SelectItem width="90%" mode="multiple" placeholder="Todos los proveedores">
                          {this.state.objetoProveedores}
                        </SelectItem>
                      </FormItem>
                    </Col>
                  </Row>
                </TabPane>
              </Tabs>
            </div>

            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                borderRadius: 5,
                marginTop: 10,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <Collapse>
                <Panel
                  header="Leyenda: qué significa cada campo del reporte"
                  key="leyenda"
                  extra={<QuestionCircleOutlined style={{ color: "#8c8c8c" }} />}
                >
                  {LEYENDA.map((seccion) => (
                    <div key={seccion.seccion} style={{ marginBottom: 18 }}>
                      <h2 style={{ fontSize: 14, fontWeight: 700, marginBottom: 6 }}>
                        {seccion.seccion}
                      </h2>
                      <Table
                        size="small"
                        rowKey="campo"
                        columns={COLUMNAS_LEYENDA}
                        dataSource={seccion.campos}
                        pagination={false}
                        showHeader={false}
                      />
                      {seccion.nota && (
                        <p style={{ fontSize: 12, color: "#8c8c8c", margin: "6px 0 0" }}>
                          {seccion.nota}
                        </p>
                      )}
                    </div>
                  ))}
                </Panel>
              </Collapse>
            </div>

            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                borderRadius: 5,
                marginTop: 10,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <h1 style={{ fontSize: 16, marginBottom: 12 }}>Resumen por Proveedor</h1>
              <TableItem
                size="small"
                rowKey={(registro) => registro.contraparteId}
                columns={columnasResumen}
                dataSource={resumen}
                defaultPageSize={50}
                summary={() =>
                  resumen.length > 0 ? this.filaResumenTotales(columnasResumen, resumen) : null
                }
              />
            </div>

            <div
              style={{
                backgroundColor: "white",
                padding: 20,
                borderRadius: 5,
                marginTop: 10,
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
              }}
            >
              <Collapse>
                <Panel header={`Papel de trabajo (${papelTrabajo.length} movimientos)`} key="pt">
                  <TableItem
                    size="small"
                    rowKey={(registro, indice) =>
                      `${registro.pagoId}-${registro.documentoId}-${indice}`
                    }
                    columns={columnasPapel}
                    dataSource={papelTrabajo}
                    defaultPageSize={25}
                    expandable={{
                      expandedRowRender: (registro) => (
                        <>
                          {registro.retenciones && registro.retenciones.length > 0 && (
                            <Table
                              size="small"
                              rowKey={(retencion, indice) =>
                                `${registro.documentoId}-retencion-${indice}`
                              }
                              columns={COLUMNAS_RETENCIONES}
                              dataSource={registro.retenciones}
                              pagination={false}
                              scroll={{ x: "max-content" }}
                              style={{ marginBottom: 12 }}
                            />
                          )}
                          {registro.insumos && registro.insumos.length > 0 && (
                            <Table
                              size="small"
                              rowKey={(insumo, indice) => `${registro.documentoId}-insumo-${indice}`}
                              columns={COLUMNAS_INSUMOS}
                              dataSource={registro.insumos}
                              pagination={false}
                              scroll={{ x: "max-content" }}
                            />
                          )}
                        </>
                      ),
                      rowExpandable: (registro) =>
                        (registro.insumos && registro.insumos.length > 0) ||
                        (registro.retenciones && registro.retenciones.length > 0),
                    }}
                    summary={() =>
                      papelTrabajo.length > 0
                        ? this.filaResumenTotales(columnasPapel, papelTrabajo, true)
                        : null
                    }
                  />
                </Panel>
              </Collapse>
            </div>
          </Spin>
        </Form>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  empresa: state.empresa.nombre,
});

export default connect(mapStateToProps)(ReporteRetencionesCompras);
